import React from 'react'
import { toast } from 'react-toastify'
import { LoadingButton } from '@mui/lab'
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import EsimAPI from 'api/faber/EsimAPI'
import MenuClose from 'assets/icons/MenuClose.png'

interface Props {
  faberToken: string;
  planId: string;
  open: boolean;
  onClose: () => void;
}

function TerminateVoicePlanDialog({
  faberToken,
  planId,
  open,
  onClose,
}: Props) {
  const queryClient = useQueryClient();

  const { mutate, isLoading } = useMutation(
    EsimAPI.terminateCustomerVoicePlan,
    {
      onSuccess: () => {
        toast.success('Plan termination successful')
        queryClient.refetchQueries({ queryKey: ['customer-voice-plans'] });
      },
      onError: () => {
        toast.error('Error in terminating voice plan')
      },
      onSettled: () => {
        onClose()
      },
    }
  )

  const handleTermination = () => {
    if (planId) {
      mutate({ planId, faberToken })
    } else {
      toast.error('No plan selected')
    }
  }

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth={true}
      onClose={onClose}
      sx={{ '& .MuiDialog-paper': { borderRadius: '12px' } }}
    >
      <DialogTitle
        sx={{
          fontWeight: '700',
          fontSize: '20px',
          color: '#333',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        Terminate Voice Plan
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            marginRight: '-8px',
            marginTop: '-8px',
          }}
        >
          <img
            style={{
              width: '20px',
              height: '20px',
            }}
            src={MenuClose}
            alt="Close"
          />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Typography
          variant='h4'
          sx={{
            fontWeight: '700',
            color: '#333',
            display: 'flex',
          }}
        >
          Do you really want to terminate the package?
        </Typography>
        <Typography
          variant='body1'
          sx={{
            color: 'red',
            display: 'flex',
          }}
        >
          This will terminate the pack immediately and cannot be undone. Please proceed with caution
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{
          padding: '16px',
          display: 'flex',
        }}
      >
        <LoadingButton variant='text' onClick={onClose}>
          Cancel
        </LoadingButton>

        <LoadingButton
          variant="contained"
          onClick={handleTermination}
          loading={isLoading}
          loadingIndicator={<CircularProgress size={24} />}
          sx={{
            paddingX: 4,
            fontWeight: 'bold',
          }}
        >
          Confirm
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default TerminateVoicePlanDialog